import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import {
    Alert,
    Button,
    Collapse,
    Input,
    List,
    Radio,
    Select,
    Tag,
    Typography,
    message,
} from 'antd'
import { marked } from 'marked'
import { ContentPath, hashContentPath } from 'common/src/ContentPath'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import {
    AgentAccountHealth,
    AgentContentJob,
    AgentContentJobMode,
    AgentContentJobStatus,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { agentActivityTabAtom } from '@/atoms/agentActivityTab'
import {
    slimLeafNodeAtomFamily,
    slimLeafNodeInvalidationAtomFamily,
} from '@/atoms/slimLeafNode'
import { invalidate } from '@/atoms/types'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import { useInterval } from '@/hooks/useInterval'
import {
    cancelAgentContentJob,
    getAgentContentJob,
    getAgentInstructorContext,
    listAgentContentJobsForCourse,
    listAgentContentJobsForSection,
    setAgentInstructorContext,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import styles from './AgentContentEditor.module.less'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    AGENT_MODE_LABEL,
    AGENT_STATUS_TAG_COLOR,
    getAgentModelLabel,
} from '@/components/GlobalAgentActivity/agentActivityLabels'

export interface AgentContentEditorProps {
    contentPath: ContentPath
}

const POLL_INTERVAL_MS = 5_000
const ELAPSED_TICK_MS = 1_000
const RECENTLY_FINISHED_WINDOW_MS = 60 * 60 * 1_000
const DEFAULT_AGENT_MODEL = AnthropicModelName['claude-opus-5-5']

const isJobActive = (job: AgentContentJob | null): boolean =>
    !!job &&
    (job.status === AgentContentJobStatus.queued ||
        job.status === AgentContentJobStatus.waitingForLock ||
        job.status === AgentContentJobStatus.running)

const MODE_DESCRIPTION: Record<AgentContentJobMode, string> = {
    [AgentContentJobMode.fillGaps]:
        'The agent compares existing content against the source material and adds, corrects, or dedupes items. Existing content is the baseline.',
    [AgentContentJobMode.fromScratch]:
        'The agent replaces ALL content in this section (outline, summary, notecards, practice problems) with a fresh set authored from the source material.',
    [AgentContentJobMode.adHoc]:
        'The agent carries out exactly the request you type below — nothing more.',
    [AgentContentJobMode.counterpartBackfill]:
        'Author missing notecard counterparts (and missing mnemonics) only — no other content changes.',
    [AgentContentJobMode.formulaSheet]:
        'Author or revise formula sheet entries for a section or the full course.',
    [AgentContentJobMode.sourceMaterialImport]:
        'Import source material from files/URLs (run from the Source Materials panel).',
    [AgentContentJobMode.previousExamAuthoring]:
        'Author practice problems from a stored SOA exam file set (run from the Previous Exams panel).',
    [AgentContentJobMode.courseWideEdit]:
        'Apply one instruction across the ENTIRE course (run from the Course-Wide AI Edit button on the dashboard).',
}

const jobStatusLabel = (status: AgentContentJobStatus): string =>
    status === AgentContentJobStatus.canceled
        ? 'Stopped'
        : status === AgentContentJobStatus.waitingForLock
          ? 'Waiting for lock'
          : status

const formatElapsed = (elapsedMs: number): string => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1_000))
    const minutes = Math.floor(totalSeconds / 60)
    if (minutes >= 60) {
        return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
    }
    return `${minutes}m ${totalSeconds % 60}s`
}

// Isolated so the 1s tick re-renders only this span, not the whole modal
const RunningElapsed: React.FC<{ createdAt: string }> = (
    props
): ReactElement => {
    const [nowMs, setNowMs] = useState<number>(() => Date.now())
    useInterval(() => setNowMs(Date.now()), ELAPSED_TICK_MS)
    return (
        <>
            {' — running for '}
            {formatElapsed(nowMs - new Date(props.createdAt).getTime())}
        </>
    )
}

const EXCERPT_REQUEST_CHARS = 240
const EXCERPT_SUMMARY_CHARS = 400

const excerpt = (text: string, maxChars: number): string =>
    text.length > maxChars ? `${text.slice(0, maxChars)}…` : text

export const AgentContentEditor: React.FC<AgentContentEditorProps> = (
    props
): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const [mode, setMode] = useState<AgentContentJobMode>(
        AgentContentJobMode.fillGaps
    )
    const [modelName, setModelName] =
        useState<AnthropicModelName>(DEFAULT_AGENT_MODEL)
    const [isStarting, setIsStarting] = useState<boolean>(false)
    const [isStopping, setIsStopping] = useState<boolean>(false)
    const [job, setJob] = useState<AgentContentJob | null>(null)
    const activity = useRecoilValue(agentActivityAtom)
    const [hasRecoveredJob, setHasRecoveredJob] = useState<boolean>(false)
    const [instructorRequest, setInstructorRequest] = useState<string>('')
    const [contextMarkdown, setContextMarkdown] = useState<string>('')
    const [savedContextMarkdown, setSavedContextMarkdown] = useState<string>('')
    const [isSavingContext, setIsSavingContext] = useState<boolean>(false)
    const [courseJobs, setCourseJobs] = useState<AgentContentJob[]>([])

    useEffect(() => {
        void (async () => {
            const response = await getAgentInstructorContext()
            if (response.data.success) {
                setContextMarkdown(response.data.payload.contextMarkdown)
                setSavedContextMarkdown(response.data.payload.contextMarkdown)
            }
        })()
    }, [])

    useEffect(() => {
        void (async () => {
            const response = await listAgentContentJobsForCourse({
                courseName,
                limit: 15,
            })
            if (response.data.success) {
                setCourseJobs(response.data.payload.jobs)
            }
        })()
    }, [courseName, job?.status])

    const setViewingState = useSetRecoilState(viewingStateAtom)
    const setAgentActivityTab = useSetRecoilState(agentActivityTabAtom)
    // Hand off to the global Agent Activity surfaces (richer than this list:
    // cross-course history, live logs, change diffs). Switching the viewing
    // type closes this editor; browser back returns to it.
    const openRunDetails = useCallback(
        (jobId: string): void => {
            setViewingState((viewingState) => ({
                ...viewingState,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            }))
        },
        [setViewingState]
    )
    const openFullHistory = useCallback((): void => {
        setAgentActivityTab('history')
        setViewingState((viewingState) => ({
            ...viewingState,
            viewingStateType: ViewingStateType.agentActivity,
            id: null,
        }))
    }, [setAgentActivityTab, setViewingState])

    const saveInstructorContext = useCallback(async (): Promise<void> => {
        setIsSavingContext(true)
        try {
            const response = await setAgentInstructorContext({
                contextMarkdown,
            })
            if (response.data.success) {
                setSavedContextMarkdown(contextMarkdown)
                syncSuccessMessage('Agent context saved.')
            } else {
                syncErrorMessage(
                    `Failed to save agent context: ${response.data.error}`
                )
            }
        } finally {
            setIsSavingContext(false)
        }
    }, [contextMarkdown])

    const hashedContentPath = useMemo(
        (): string => hashContentPath(props.contentPath),
        [props.contentPath]
    )
    const resetSlimLeafNodeAtomFamily = useResetRecoilState(
        slimLeafNodeAtomFamily(hashedContentPath)
    )
    const setSlimLeafNodeInvalidationAtomFamily = useSetRecoilState(
        slimLeafNodeInvalidationAtomFamily(hashedContentPath)
    )
    const invalidateSlimLeafNode = useCallback((): void => {
        setSlimLeafNodeInvalidationAtomFamily((val) => invalidate(val))
        resetSlimLeafNodeAtomFamily()
    }, [resetSlimLeafNodeAtomFamily, setSlimLeafNodeInvalidationAtomFamily])

    // Recover an in-flight or recent job after remount / page reload: always
    // re-attach to an active job; also surface a run that finished recently so
    // its summary isn't lost to a reload
    useEffect(() => {
        if (hasRecoveredJob) return
        setHasRecoveredJob(true)
        void (async () => {
            const response = await listAgentContentJobsForSection({
                courseName,
                contentPath: props.contentPath,
            })
            if (response.data.success && response.data.payload.jobs.length) {
                const mostRecentJob = response.data.payload.jobs[0]
                const finishedRecently =
                    Date.now() - new Date(mostRecentJob.updatedAt).getTime() <
                    RECENTLY_FINISHED_WINDOW_MS
                if (isJobActive(mostRecentJob) || finishedRecently) {
                    setJob(mostRecentJob)
                }
            }
        })()
    }, [courseName, hasRecoveredJob, props.contentPath])

    const refreshJob = useCallback(async (): Promise<void> => {
        if (!job) return
        const response = await getAgentContentJob({ jobId: job.id })
        if (!response.data.success) return
        const updatedJob = response.data.payload.job
        const justFinished = isJobActive(job) && !isJobActive(updatedJob)
        setJob(updatedJob)
        if (justFinished) {
            if (updatedJob.status === AgentContentJobStatus.succeeded) {
                syncSuccessMessage(
                    'Agent run complete. The section content has been updated.'
                )
            }
            invalidateSlimLeafNode()
        }
    }, [invalidateSlimLeafNode, job])

    useInterval(
        () => void refreshJob(),
        isJobActive(job) ? POLL_INTERVAL_MS : null
    )

    const startJob = useCallback(async (): Promise<void> => {
        const trimmedRequest = instructorRequest.trim()
        if (mode === AgentContentJobMode.adHoc && !trimmedRequest) {
            syncErrorMessage('Ad hoc mode needs a request to carry out.')
            return
        }
        setIsStarting(true)
        try {
            // The run reads the SAVED context server-side — persist dirty edits
            // first so what's on screen is always what the agent sees
            if (contextMarkdown !== savedContextMarkdown) {
                const saveResponse = await setAgentInstructorContext({
                    contextMarkdown,
                })
                if (!saveResponse.data.success) {
                    syncErrorMessage(
                        `Could not save your agent context, so the run was not started: ${saveResponse.data.error}`
                    )
                    return
                }
                setSavedContextMarkdown(contextMarkdown)
            }
            const response = await startAgentContentJob({
                courseName,
                contentPath: props.contentPath,
                mode,
                modelName,
                instructorRequest: trimmedRequest || undefined,
            })
            if (!response.data.success) {
                syncErrorMessage(
                    `Failed to start agent job: ${response.data.error}`
                )
                return
            }
            const jobResponse = await getAgentContentJob({
                jobId: response.data.payload.jobId,
            })
            if (jobResponse.data.success) {
                setJob(jobResponse.data.payload.job)
            }
            message.success({
                content: (
                    <span>
                        {response.data.payload.waitingOnJobId
                            ? 'Run queued behind another job — it starts automatically when that run finishes. '
                            : 'Agent run started. '}
                        <Button
                            type={'link'}
                            size={'small'}
                            onClick={() =>
                                openRunDetails(response.data.payload.jobId)
                            }
                        >
                            View run
                        </Button>
                    </span>
                ),
                duration: 8,
            })
        } finally {
            setIsStarting(false)
        }
    }, [
        contextMarkdown,
        courseName,
        instructorRequest,
        mode,
        modelName,
        openRunDetails,
        props.contentPath,
        savedContextMarkdown,
    ])

    const stopJob = useCallback(async (): Promise<void> => {
        if (!job || !isJobActive(job)) return
        if (!window.confirm('Stop this agent job? Progress will be discarded.'))
            return
        setIsStopping(true)
        try {
            const response = await cancelAgentContentJob({ jobId: job.id })
            if (response.data.success) {
                setJob(response.data.payload.job)
                syncSuccessMessage('Agent job stopped.')
            } else {
                syncErrorMessage(
                    `Failed to stop agent job: ${response.data.error}`
                )
            }
        } finally {
            setIsStopping(false)
        }
    }, [job])

    const counts = job?.summary?.counts

    return (
        <div className={styles.agentContentEditor}>
            <Typography.Title level={4}>
                <AiSparkleIcon /> AI Agent Content Generation
            </Typography.Title>
            {activity && (
                <div className={styles.activityStrip}>
                    <div className={styles.activitySummary}>
                        <Typography.Text>
                            Agents: {activity.instructorPool.running} of{' '}
                            {activity.instructorPool.cap} running ·{' '}
                            {activity.instructorPool.queued} queued
                        </Typography.Text>
                        {activity.accounts
                            .filter(
                                (account) =>
                                    account.health !==
                                    AgentAccountHealth.healthy
                            )
                            .map((account) => (
                                <Tag
                                    key={account.name}
                                    color={'warning'}
                                    title={
                                        account.coolingDownUntil
                                            ? `Cooling down until ${new Date(
                                                  account.coolingDownUntil
                                              ).toLocaleString()}`
                                            : undefined
                                    }
                                >
                                    ⚠ account &quot;{account.name}&quot;{' '}
                                    {account.health}
                                </Tag>
                            ))}
                    </div>
                    {activity.activeJobs.length > 0 && (
                        <div className={styles.activeJobs}>
                            {activity.activeJobs
                                .slice(0, 5)
                                .map((activeJob) => {
                                    const isThisSection =
                                        activeJob.jobId === job?.id
                                    return (
                                        <div
                                            key={activeJob.jobId}
                                            className={
                                                isThisSection
                                                    ? styles.currentActiveJob
                                                    : styles.activeJob
                                            }
                                        >
                                            {activeJob.courseName} —{' '}
                                            {activeJob.requestedByDisplayName ??
                                                'unknown'}{' '}
                                            — {AGENT_MODE_LABEL[activeJob.mode]}
                                            {' · '}
                                            {getAgentModelLabel(
                                                activeJob.modelName
                                            )}
                                            {' · '}
                                            <Tag
                                                color={
                                                    AGENT_STATUS_TAG_COLOR[
                                                        activeJob.status
                                                    ]
                                                }
                                            >
                                                {jobStatusLabel(
                                                    activeJob.status
                                                )}
                                            </Tag>
                                            {isThisSection && (
                                                <Tag color={'blue'}>
                                                    this section
                                                </Tag>
                                            )}
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                </div>
            )}
            <div className={styles.controls}>
                <Radio.Group
                    value={mode}
                    onChange={(event) => setMode(event.target.value)}
                    disabled={isReadOnly || isJobActive(job)}
                >
                    {Object.values(AgentContentJobMode)
                        .filter(
                            // Only the section-scoped modes belong on this
                            // per-section picker; the others run from their
                            // own surfaces
                            (jobMode) =>
                                jobMode !==
                                    AgentContentJobMode.counterpartBackfill &&
                                jobMode !== AgentContentJobMode.formulaSheet &&
                                jobMode !==
                                    AgentContentJobMode.sourceMaterialImport &&
                                jobMode !==
                                    AgentContentJobMode.previousExamAuthoring &&
                                jobMode !== AgentContentJobMode.courseWideEdit
                        )
                        .map((jobMode) => (
                            <Radio.Button key={jobMode} value={jobMode}>
                                {AGENT_MODE_LABEL[jobMode]}
                            </Radio.Button>
                        ))}
                </Radio.Group>
                <Select<AnthropicModelName>
                    value={modelName}
                    onChange={setModelName}
                    disabled={isReadOnly || isJobActive(job)}
                    options={[
                        AnthropicModelName['claude-opus-5-5'],
                        AnthropicModelName['claude-fable-5'],
                        AnthropicModelName['claude-sonnet-5-5'],
                    ].map((model) => ({ value: model, label: model }))}
                    className={styles.modelSelect}
                />
                <Button
                    type={'primary'}
                    loading={
                        isStarting ||
                        job?.status === AgentContentJobStatus.running
                    }
                    disabled={isReadOnly}
                    onClick={() => void startJob()}
                >
                    {job?.status === AgentContentJobStatus.waitingForLock
                        ? 'Waiting for another run...'
                        : isJobActive(job)
                          ? 'Agent running...'
                          : 'Start agent'}
                </Button>
            </div>
            {isReadOnly && (
                <Typography.Text type={'secondary'}>
                    You have read-only access to this course, so you can't start
                    agent runs.
                </Typography.Text>
            )}
            <Input.TextArea
                value={instructorRequest}
                onChange={(event) => setInstructorRequest(event.target.value)}
                disabled={isReadOnly || isJobActive(job)}
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder={
                    mode === AgentContentJobMode.adHoc
                        ? 'What should the agent do? (required — e.g. "Add 3 notecards on the Value Stack and fix the rounding in problem 4")'
                        : 'Optional guidance for this run (e.g. "focus on the MSK case study; keep it under 12 cards")'
                }
            />
            <Typography.Paragraph type={'secondary'}>
                {MODE_DESCRIPTION[mode]} A checkpoint is taken automatically
                before and after changes — see the Checkpoints panel to roll
                back.
            </Typography.Paragraph>
            <Collapse>
                <Collapse.Panel
                    key={'context'}
                    header={
                        'Standing agent context (yours — applies to every run you start)'
                    }
                >
                    <div className={styles.contextPanel}>
                        <Input.TextArea
                            value={contextMarkdown}
                            onChange={(event) =>
                                setContextMarkdown(event.target.value)
                            }
                            autoSize={{ minRows: 4, maxRows: 12 }}
                            placeholder={
                                'Standing guidance injected into every agent run you start, across all sections and courses — style preferences, pet peeves, standing rules.'
                            }
                        />
                        <Button
                            type={'primary'}
                            loading={isSavingContext}
                            onClick={() => void saveInstructorContext()}
                        >
                            Save context
                        </Button>
                    </div>
                </Collapse.Panel>
                <Collapse.Panel
                    key={'history'}
                    header={`Recent agent runs in this course (${courseJobs.length})`}
                >
                    <Button
                        onClick={openFullHistory}
                        size={'small'}
                        style={{ marginBottom: 8 }}
                    >
                        Open full history (all courses, with change diffs)
                    </Button>
                    <List
                        dataSource={courseJobs}
                        locale={{
                            emptyText: 'No agent runs in this course yet.',
                        }}
                        renderItem={(courseJob) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key={'details'}
                                        onClick={() =>
                                            openRunDetails(courseJob.id)
                                        }
                                        size={'small'}
                                    >
                                        Details
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={
                                        <span>
                                            <Tag>
                                                {
                                                    AGENT_MODE_LABEL[
                                                        courseJob.mode
                                                    ]
                                                }
                                            </Tag>
                                            <Tag>
                                                {getAgentModelLabel(
                                                    courseJob.modelName
                                                )}
                                            </Tag>
                                            <Tag
                                                color={
                                                    AGENT_STATUS_TAG_COLOR[
                                                        courseJob.status
                                                    ]
                                                }
                                            >
                                                {jobStatusLabel(
                                                    courseJob.status
                                                )}
                                            </Tag>{' '}
                                            —{' '}
                                            {courseJob.requestedByDisplayName ??
                                                'unknown'}{' '}
                                            ·{' '}
                                            {new Date(
                                                courseJob.createdAt
                                            ).toLocaleString()}
                                        </span>
                                    }
                                    description={
                                        <>
                                            {courseJob.instructorRequest && (
                                                <Typography.Paragraph>
                                                    Asked:{' '}
                                                    {excerpt(
                                                        courseJob.instructorRequest,
                                                        EXCERPT_REQUEST_CHARS
                                                    )}
                                                </Typography.Paragraph>
                                            )}
                                            {courseJob.summary
                                                ?.changeSummaryMarkdown && (
                                                <Typography.Paragraph
                                                    type={'secondary'}
                                                >
                                                    {excerpt(
                                                        courseJob.summary
                                                            .changeSummaryMarkdown,
                                                        EXCERPT_SUMMARY_CHARS
                                                    )}
                                                </Typography.Paragraph>
                                            )}
                                        </>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </Collapse.Panel>
            </Collapse>

            {job && (
                <div className={styles.jobPanel}>
                    <Typography.Text strong>
                        Job {job.id.slice(0, 8)} — {jobStatusLabel(job.status)}{' '}
                        ({job.mode}, {job.modelName})
                        {job.status === AgentContentJobStatus.running && (
                            <RunningElapsed createdAt={job.createdAt} />
                        )}
                    </Typography.Text>
                    {isJobActive(job) && (
                        <Button
                            danger
                            loading={isStopping}
                            disabled={isReadOnly || isStopping}
                            onClick={() => void stopJob()}
                            className={styles.stopButton}
                        >
                            Stop
                        </Button>
                    )}
                    {job.status === AgentContentJobStatus.waitingForLock ? (
                        <Alert
                            type={'warning'}
                            showIcon
                            message={
                                'Waiting for another run to finish — this job starts automatically when the lock frees.'
                            }
                            action={
                                <Button
                                    size={'small'}
                                    onClick={() => openRunDetails(job.id)}
                                >
                                    View run
                                </Button>
                            }
                        />
                    ) : isJobActive(job) ? (
                        <div className={styles.progressLog}>
                            {job.progressMessages
                                .slice(-15)
                                .map((message, index) => (
                                    <div
                                        key={index}
                                        className={styles.progressLine}
                                    >
                                        {message}
                                    </div>
                                ))}
                            <Typography.Text type={'secondary'}>
                                This can take 10-30 minutes. You can close this
                                window; the job keeps running.
                            </Typography.Text>
                        </div>
                    ) : null}
                    {job.status === AgentContentJobStatus.succeeded &&
                        job.summary && (
                            <Alert
                                type={'success'}
                                showIcon
                                message={
                                    counts
                                        ? `Notecards: +${counts.notecardsAdded} / ~${counts.notecardsReplaced} / -${counts.notecardsDeleted} · Practice problems: +${counts.practiceProblemsAdded} / ~${counts.practiceProblemsReplaced} / -${counts.practiceProblemsDeleted}` +
                                          `${(counts.counterpartsAdded ?? 0) || (counts.counterpartsReplaced ?? 0) || (counts.counterpartsDeleted ?? 0) ? ` · Counterparts: +${counts.counterpartsAdded ?? 0} / ~${counts.counterpartsReplaced ?? 0} / -${counts.counterpartsDeleted ?? 0}` : ''}` +
                                          `${counts.outlineChanged ? ' · outline updated' : ''}${counts.summaryChanged ? ' · summary updated' : ''}`
                                        : 'Agent run complete.'
                                }
                                description={
                                    <div
                                        className={styles.changeSummary}
                                        dangerouslySetInnerHTML={{
                                            __html: marked(
                                                job.summary
                                                    .changeSummaryMarkdown,
                                                {}
                                            ),
                                        }}
                                    />
                                }
                            />
                        )}
                    {(job.status === AgentContentJobStatus.failed ||
                        job.status === AgentContentJobStatus.orphaned) && (
                        <Alert
                            type={'error'}
                            showIcon
                            message={`Agent job ${job.status}`}
                            description={job.error}
                        />
                    )}
                    {job.status === AgentContentJobStatus.canceled && (
                        <Alert type={'info'} showIcon message={'Stopped'} />
                    )}
                </div>
            )}
        </div>
    )
}
